import { useMutation } from '@apollo/client';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import { useConfirmationDialog } from '@openx/components-core/lib/Dialog/ConfirmationDialog/useConfirmationDialog';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { DeleteInventoryListDocument } from '../../../../graphql/mutation/inventoryList/deleteInventoryList.generated';

type HandleDeleteModalProps = {
  id: string;
  name: string;
};

export const useDeleteInventoryList = () => {
  const { open, setLoading, close } = useConfirmationDialog();
  const [deleteInventoryList, { loading }] = useMutation(DeleteInventoryListDocument);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const handleDelete = useCallback(
    async (providedId: string) => {
      await deleteInventoryList({
        variables: { id: parseInt(providedId, 10) },
        refetchQueries: ['GetInventoryListsList'],
        onCompleted: () => {
          enqueueSnackbar(t('Inventory list: {id} has been deleted.', { id: providedId }), { variant: 'success' });
          navigate('/inventory-lists');
        },
      });
    },
    [deleteInventoryList, enqueueSnackbar, navigate, t]
  );

  const handleDeleteModal = useCallback(
    ({ id, name }: HandleDeleteModalProps) =>
      open({
        confirmationButtonColor: 'error',
        confirmationLabel: t('Delete'),
        content: (
          <DialogContent dividers>
            <Typography>{t('You’re about to delete the following Inventory List:')}</Typography>
            <Typography variant="body1Bold" sx={{ mb: 3 }}>
              {name}
            </Typography>
            <Typography>{t('Do you want to proceed?')}</Typography>
          </DialogContent>
        ),
        customContent: true,
        onConfirmed: async () => {
          setLoading(true);
          try {
            await handleDelete(id);
          } finally {
            setLoading(false);
            close();
          }
        },
        title: t('Delete Inventory List'),
      }),
    [close, handleDelete, open, setLoading, t]
  );

  return {
    deleteInventoryList: handleDeleteModal,
    loading,
  };
};
